// /home/labdoo/zitech-nexus/frontend/src/pages/admin/portfolio/AdminProjectsList.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { portfolioAPI } from '../../../services/api';
import './PortfolioAdmin.css';

// SVG Icons
const Icons = {
  Plus: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <path d="M12 5V19M5 12H19" />
    </svg>
  ),
  Edit: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 3L21 7L7 21H3V17L17 3Z" />
    </svg>
  ),
  Delete: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 7H20" />
      <path d="M10 11V16" />
      <path d="M14 11V16" />
      <path d="M5 7L6 19C6 19.5304 6.21071 20.0391 6.58579 20.4142C6.96086 20.7893 7.46957 21 8 21H16C16.5304 21 17.0391 20.7893 17.4142 20.4142C17.7893 20.0391 18 19.5304 18 19L19 7" />
      <path d="M9 7L10 3H14L15 7" />
    </svg>
  ),
  Refresh: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23 4 23 10 17 10" />
      <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
    </svg>
  ),
  Search: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  ),
  Star: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
    </svg>
  ),
  StarOutline: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
    </svg>
  ),
  Spinner: () => (
    <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#0d47a1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="pa-spinner-svg">
      <path d="M12 2v4M12 22v-4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M22 12h-4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
  ),
  Check: () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <path d="M20 6L9 17L4 12" />
    </svg>
  ),
  X: () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  Empty: () => (
    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="7" width="18" height="14" rx="2" />
      <path d="M8 7V5C8 3.89543 8.89543 3 10 3H14C15.1046 3 16 3.89543 16 5V7" />
      <path d="M12 12V16M10 14H14" />
    </svg>
  ),
  Calendar: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  ),
};

interface Project {
  id: string;
  title: string;
  category: string;
  category_display: string;
  description: string;
  client: string;
  year: string;
  is_featured: boolean;
  is_published: boolean;
  order: number;
  created_at: string;
}

const AdminProjectsList: React.FC = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'published' | 'unpublished'>('all');
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const response = await portfolioAPI.adminGetProjects();
      setProjects(response.data);
    } catch (err: any) {
      console.error('Error fetching projects:', err);
      setError('Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects, refreshKey]);

  const refreshData = () => {
    setRefreshKey(prev => prev + 1);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this project? This action cannot be undone.')) {
      try {
        await portfolioAPI.adminDeleteProject(id);
        await refreshData();
        alert('Project deleted successfully!');
      } catch (err: any) {
        console.error('Error deleting project:', err);
        alert('Failed to delete project');
      }
    }
  };

  const handleToggleFeature = async (id: string, currentStatus: boolean) => {
    setUpdatingId(id);
    try {
      await portfolioAPI.adminUpdateProject(id, { is_featured: !currentStatus });
      await new Promise(resolve => setTimeout(resolve, 500));
      await refreshData();
    } catch (err: any) {
      console.error('Error toggling feature:', err);
      alert('Failed to update project featured status');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleTogglePublish = async (id: string, currentStatus: boolean) => {
    setUpdatingId(id);
    try {
      await portfolioAPI.adminUpdateProject(id, { is_published: !currentStatus });
      await new Promise(resolve => setTimeout(resolve, 500));
      await refreshData();
    } catch (err: any) {
      console.error('Error toggling publish:', err);
      alert('Failed to update project status');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleOrderChange = async (id: string, newOrder: number) => {
    if (isNaN(newOrder)) return;
    setUpdatingId(id);
    try {
      await portfolioAPI.adminUpdateProject(id, { order: newOrder });
      await new Promise(resolve => setTimeout(resolve, 500));
      await refreshData();
    } catch (err: any) {
      console.error('Error updating order:', err);
      alert('Failed to update project order');
    } finally {
      setUpdatingId(null);
    }
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, { bg: string; text: string }> = {
      web_development: { bg: '#dbeafe', text: '#1e40af' },
      mobile_development: { bg: '#d1fae5', text: '#065f46' },
      software_integration: { bg: '#fed7aa', text: '#92400e' },
      data_analytics: { bg: '#e0e7ff', text: '#3730a3' },
      cloud_services: { bg: '#dbeafe', text: '#1e40af' },
      ai_ml: { bg: '#f3e8ff', text: '#6b21a5' },
    };
    return colors[category] || { bg: '#f3f4f6', text: '#4b5563' };
  };

  const getStatusColor = (isPublished: boolean) => {
    return isPublished ? '#10b981' : '#f59e0b';
  };

  const getStatusBackground = (isPublished: boolean) => {
    return isPublished ? '#d1fae5' : '#fef3c7';
  };

  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          project.client.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          project.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !filterCategory || project.category === filterCategory;
    const matchesStatus = filterStatus === 'all' || 
                         (filterStatus === 'published' && project.is_published) ||
                         (filterStatus === 'unpublished' && !project.is_published);
    return matchesSearch && matchesCategory && matchesStatus;
  });

  const categories = Array.from(new Set(projects.map(p => p.category)));
  const stats = {
    total: projects.length,
    published: projects.filter(p => p.is_published).length,
    unpublished: projects.filter(p => !p.is_published).length,
    featured: projects.filter(p => p.is_featured).length,
  };

  if (loading) {
    return (
      <div className="pa-loading-container">
        <div className="pa-spinner">
          <Icons.Spinner />
        </div>
        <p className="pa-loading-text">Loading projects...</p>
      </div>
    );
  }

  return (
    <div className="pa-container">
      {/* Header */}
      <div className="pa-header">
        <div className="pa-header-left">
          <h1 className="pa-title">Manage Projects</h1>
          <p className="pa-subtitle">Create and manage your portfolio projects</p>
        </div>
        <button 
          className="pa-btn-primary"
          onClick={() => navigate('/dashboard/admin/portfolio/projects/create')}
        >
          <Icons.Plus />
          Create New Project
        </button>
      </div>

      {/* Stats Bar */}
      <div className="pa-stats-bar">
        <div className="pa-stat-item">
          <span className="pa-stat-label">Total</span>
          <span className="pa-stat-value">{stats.total}</span>
        </div>
        <div className="pa-stat-divider"></div>
        <div className="pa-stat-item">
          <span className="pa-stat-label">Published</span>
          <span className="pa-stat-value pa-stat-published">{stats.published}</span>
        </div>
        <div className="pa-stat-divider"></div>
        <div className="pa-stat-item">
          <span className="pa-stat-label">Unpublished</span>
          <span className="pa-stat-value pa-stat-unpublished">{stats.unpublished}</span>
        </div>
        <div className="pa-stat-divider"></div>
        <div className="pa-stat-item">
          <span className="pa-stat-label">Featured</span>
          <span className="pa-stat-value pa-stat-featured">{stats.featured}</span>
        </div>
      </div>

      {/* Filters */}
      <div className="pa-filters">
        <div className="pa-search">
          <Icons.Search />
          <input
            type="text"
            placeholder="Search projects by title, client, or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="pa-filter-group">
          <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
            <option value="">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat.replace('_', ' ').toUpperCase()}</option>
            ))}
          </select>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value as any)}>
            <option value="all">All Status</option>
            <option value="published">Published Only</option>
            <option value="unpublished">Unpublished Only</option>
          </select>
          <button className="pa-refresh-btn" onClick={refreshData} disabled={loading}>
            <Icons.Refresh />
            Refresh
          </button>
        </div>
      </div>

      {error && <div className="pa-error">{error}</div>}

      {/* Table */}
      <div className="pa-table-wrapper">
        {filteredProjects.length === 0 ? (
          <div className="pa-empty-state">
            <Icons.Empty />
            <h3 className="pa-empty-title">No projects found</h3>
            <p className="pa-empty-subtitle">Try adjusting your search or filters, or create a new project.</p>
            <button className="pa-btn-primary" onClick={() => navigate('/dashboard/admin/portfolio/projects/create')}>
              <Icons.Plus />
              Create New Project
            </button>
          </div>
        ) : (
          <table className="pa-table">
            <thead>
              <tr>
                <th className="pa-col-title">Title</th>
                <th className="pa-col-category">Category</th>
                <th className="pa-col-client">Client</th>
                <th className="pa-col-year">Year</th>
                <th className="pa-col-status">Status</th>
                <th className="pa-col-featured">Featured</th>
                <th className="pa-col-order">Order</th>
                <th className="pa-col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredProjects.map((project) => {
                const categoryColors = getCategoryColor(project.category);
                return (
                  <tr key={`${project.id}-${project.is_published}`} className={!project.is_published ? 'pa-row-inactive' : ''}>
                    <td className="pa-col-title">
                      <span className="pa-project-title">
                        {project.title}
                        {!project.is_published && (
                          <span className="pa-draft-badge">Draft</span>
                        )}
                        {updatingId === project.id && (
                          <span className="pa-spinner-small"></span>
                        )}
                      </span>
                    </td>
                    <td className="pa-col-category">
                      <span 
                        className="pa-category-badge"
                        style={{ background: categoryColors.bg, color: categoryColors.text }}
                      >
                        {project.category_display}
                      </span>
                    </td>
                    <td className="pa-col-client">{project.client}</td>
                    <td className="pa-col-year">{project.year}</td>
                    <td className="pa-col-status">
                      <button 
                        className={`pa-status-toggle ${project.is_published ? 'pa-status-active' : 'pa-status-inactive'}`}
                        onClick={() => handleTogglePublish(project.id, project.is_published)}
                        disabled={updatingId === project.id}
                        title={project.is_published ? 'Unpublish' : 'Publish'}
                      >
                        <span 
                          className="pa-status-badge"
                          style={{
                            background: getStatusBackground(project.is_published),
                            color: getStatusColor(project.is_published)
                          }}
                        >
                          {project.is_published ? <Icons.Check /> : <Icons.X />}
                          {project.is_published ? 'Published' : 'Unpublished'}
                        </span>
                      </button>
                    </td>
                    <td className="pa-col-featured">
                      <button 
                        className={`pa-feature-btn ${project.is_featured ? 'pa-feature-active' : ''}`}
                        onClick={() => handleToggleFeature(project.id, project.is_featured)}
                        disabled={updatingId === project.id}
                        title={project.is_featured ? 'Remove from featured' : 'Mark as featured'}
                      >
                        {project.is_featured ? <Icons.Star /> : <Icons.StarOutline />}
                      </button>
                    </td>
                    <td className="pa-col-order">
                      <input 
                        type="number" 
                        value={project.order} 
                        className="pa-order-input"
                        onChange={(e) => {
                          const newOrder = parseInt(e.target.value);
                          if (!isNaN(newOrder)) {
                            handleOrderChange(project.id, newOrder);
                          }
                        }}
                        disabled={updatingId === project.id}
                      />
                    </td>
                    <td className="pa-col-actions">
                      <div className="pa-actions">
                        <button 
                          className="pa-action-btn pa-action-edit"
                          onClick={() => navigate(`/dashboard/admin/portfolio/projects/${project.id}/edit`)}
                          title="Edit project"
                        >
                          <Icons.Edit />
                          Edit
                        </button>
                        <button 
                          className="pa-action-btn pa-action-delete"
                          onClick={() => handleDelete(project.id)}
                          title="Delete project"
                          disabled={updatingId === project.id}
                        >
                          <Icons.Delete />
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="pa-footer">
        <span className="pa-footer-text">
          Showing {filteredProjects.length} of {projects.length} projects
        </span>
      </div>
    </div>
  );
};

export default AdminProjectsList;